import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import TradingView from '../../../charting_library/charting_library.standalone.js'
import { dataFeedObject, getTradingChartTimezone, stdTimezoneOffset } from './api'
import { customWidgetOptions, customWidgetOptionsWithOverrides } from './TradingChartConfig'
import { handleChartOnMessage } from '../../../utility/tradingview/helpers.js'
import { chartTypeToNumber } from '../utility/helpers'
import { useParams } from 'react-router-dom'

const TradingViewChart = ({
  hideDefaultStyleContainerStyle = false,
  containerStyle = {},
  selectedMarketId = null,
  timeframe = 240,
  chartType = 'candle',
  urlTheme,
  hideLeftToolbarByDefault = false,
  colorOverrides = null
}) => {
  const widgetRef = useRef(null)
  const { marketId } = useParams()
  // const tickersData = useSelector((state) => state.marketTickers.value)
  const selectedMarket = selectedMarketId ? { id: selectedMarketId } : useSelector(state => state.selectedMarket.value)
  const markets = useSelector(state => state.marketList.value)
  const theme = useSelector(state => state.theme.value)
  const klineData = useSelector(state => state.kline)

  const currentTimeOffset = new Date().getTimezoneOffset()
  const clockPeriod = currentTimeOffset === stdTimezoneOffset(new Date()) ? 'STD' : 'DST'

  const defaultContainerStyle = {
    borderRadius: 10,
    overflow: 'hidden',
    height: 600,
    padding: 10,
    backgroundColor: 'var(--secondary-color)',
    ...containerStyle
  }

  // Ensure we have a valid symbol
  const symbol = selectedMarket?.id || marketId || 'btcusd'

  const getWidgetOptions = () => ({
    symbol,
    interval: timeframe?.toString(),
    container_id: 'tv_chart_container',
    datafeed: dataFeedObject(TradingViewChart, markets),
    library_path: '/charting_library/',
    allow_symbol_change: false,
    autosize: true,
    calendar: true,
    hide_legend: false,
    save_image: true,
    client_id: 'tradingview.com',
    custom_css_url: '/chartStyles.css',
    debug: false,
    details: true,
    enabled_features: [
      'charts_auto_save',
      'control_bar',
      'header_in_fullscreen_mode',
      'header_chart_type',
      'header_resolutions',
      'side_toolbar_in_fullscreen_mode',
      'dont_show_boolean_study_arguments',
      'hide_last_na_study_output',
      'save_chart_properties_to_local_storage',
      'remove_library_container_border',
      hideLeftToolbarByDefault && 'hide_left_toolbar_by_default'
    ].filter(Boolean),
    disabled_features: [
      'header_widget',
      'use_localstorage_for_settings',
      'header_symbol_search',
      'symbol_search_hot_key',
      'header_compare',
      'edit_buttons_in_legend',
      'show_hide_button_in_legend',
      'legend_context_menu',
      'context_menu',
      'header_screenshot',
      'header_undo_redo',
      'go_to_date',
      'study_templates',
      'save_shortcut',
      'header_saveload',
      'timeframes_toolbar',
      'widget_logo',
      'always_show_legend_values_on_mobile',
      'format_button_in_legend',
      'delete_button_in_legend'
    ],
    timeframe: '1D',
    charts_storage_url: 'https://saveload.tradingview.com',
    charts_storage_api_version: '1.1',
    timezone: getTradingChartTimezone(currentTimeOffset, clockPeriod),
    ...(colorOverrides && Object.keys(colorOverrides).length > 0
      ? customWidgetOptionsWithOverrides(urlTheme || theme, colorOverrides)
      : customWidgetOptions(urlTheme || theme))
  })

  useEffect(() => {
    const initWidget = () => {
      if (widgetRef.current) {
        widgetRef.current.remove()
        widgetRef.current = null
      }

      const options = getWidgetOptions()
      widgetRef.current = new TradingView.widget(options)
      window.tvWidget = widgetRef.current // Keep window.tvWidget for compatibility

      widgetRef.current.onChartReady(() => {
        const chart = widgetRef.current.activeChart()
        if (chart) {
          const now = Date.now()
          const thirtyFiveDaysAgo = now - 35 * 24 * 60 * 60 * 1000
          chart.setVisibleRange({
            from: thirtyFiveDaysAgo / 1000,
            to: now / 1000
          })
        }
      })
    }

    initWidget()

    return () => {
      if (widgetRef.current) {
        widgetRef.current.remove()
        widgetRef.current = null
      }
    }
  }, [theme, symbol, colorOverrides]) // Only reinitialize on theme, symbol, or color overrides changes

  useEffect(() => {
    if (widgetRef.current && klineData.length) {
      widgetRef.current.onChartReady(() => {
        const newData = klineData.map(kline => ({
          time: kline.timestamp * 1000,
          open: kline.open,
          high: kline.high,
          low: kline.low,
          close: kline.close,
          volume: kline.volume
        }))
        widgetRef.current.activeChart().SubscribeBarsCallback(newData)
      })
    }
  }, [klineData])

  useEffect(() => {
    if (widgetRef.current) {
      widgetRef.current.onChartReady(() => {
        const options =
          colorOverrides && Object.keys(colorOverrides).length > 0
            ? customWidgetOptionsWithOverrides(urlTheme || theme, colorOverrides)
            : customWidgetOptions(urlTheme || theme)
        widgetRef.current.activeChart().applyOverrides({
          ...options.overrides
        })
      })
    }
  }, [theme, urlTheme, colorOverrides])

  useEffect(() => {
    if (widgetRef.current && timeframe) {
      widgetRef.current.onChartReady(() => {
        const chart = widgetRef.current.activeChart()
        if (chart) {
          chart.setResolution(timeframe.toString())
        }
      })
    }
  }, [timeframe])

  useEffect(() => {
    if (widgetRef.current && chartType) {
      widgetRef.current.onChartReady(() => {
        const chart = widgetRef.current.activeChart()
        if (chart) {
          chart.setChartType(chartTypeToNumber(chartType))
        }
      })
    }
  }, [chartType])

  useEffect(() => {
    window.addEventListener('message', handleChartOnMessage)
    return () => window.removeEventListener('message', handleChartOnMessage)
  }, [])

  return <div id='tv_chart_container' style={hideDefaultStyleContainerStyle ? containerStyle : defaultContainerStyle} />
}

export default TradingViewChart
